// Onerway returns 12003 when a card cannot continue through the current
// subscription payment path. For those cases, guide the user to retry with
// the JCB/Amex/UnionPay card tab instead of treating it as a terminal failure.
const ONERWAY_JCB_AE_UNIONPAY_REQUIRED_CODE = '12003'

type UnknownRecord = Record<string, unknown>

const isRecord = (value: unknown): value is UnknownRecord =>
  value !== null && typeof value === 'object'

const getStringField = (
  value: unknown,
  fields: readonly string[],
): string | undefined => {
  if (!isRecord(value)) return undefined

  for (const field of fields) {
    const fieldValue = value[field]
    if (typeof fieldValue === 'string') return fieldValue
    if (typeof fieldValue === 'number') return String(fieldValue)
  }

  return undefined
}

export const getOnerwayPaymentData = (response: unknown): unknown => {
  if (!isRecord(response)) return undefined

  const data = response.data
  if (isRecord(data)) return data

  return response
}

export const getOnerwayResponseCode = (response: unknown): string | undefined =>
  getStringField(response, ['respCode', 'responseCode'])

export const getOnerwayPaymentStatus = (
  response: unknown,
): string | undefined =>
  getStringField(getOnerwayPaymentData(response), ['status'])

export const getOnerwayPaymentStatusCode = (
  response: unknown,
): string | undefined => {
  const data = getOnerwayPaymentData(response)
  return (
    getStringField(data, [
      'statusCode',
      'respCode',
      'code',
      'responseCode',
      'resultCode',
    ]) ?? getStringField(response, ['statusCode', 'code', 'resultCode'])
  )
}

export const getOnerwayPaymentMessage = (
  response: unknown,
): string | undefined =>
  getStringField(getOnerwayPaymentData(response), ['respMsg', 'message']) ??
  getStringField(response, ['respMsg', 'message'])

export const isOnerwayJcbAeUnionPayRequired = (response: unknown): boolean =>
  getOnerwayPaymentStatus(response) === 'F' &&
  getOnerwayPaymentStatusCode(response) ===
    ONERWAY_JCB_AE_UNIONPAY_REQUIRED_CODE
